import axios from 'axios'
import _ from 'lodash'
import dayjs from 'dayjs'
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter'
import router from '@/router'
import SEARCH from '@/api/search'
// import USER from '@/api/user'
import { getField, updateField } from 'vuex-map-fields'

dayjs.extend(isSameOrAfter)

// initial state
const state = {
  isSearchBarMounted: false,
  // autoComplete
  autoComplete: {
    isLoading: false,
    value: null,
    result: null,
    callback: {}
  },
  autoCompleteCancelToken: null,
  // date
  startDate: null,
  endDate: null,
  // filters
  defaultFilters: {
    adult: 2,
    child: 0,
    childAge: [],
    roomCount: null,
    isStock: null,
    hotelUUID: null,
    placeCountry: null,
    placeCity: null,
    stars: ['0', '1', '2', '3', '4', '5'],
    hotelTypes: [],
    features: [],
    priceRange: [0, 0]
  },
  filters: {
    adult: 2,
    child: 0,
    childAge: [],
    roomCount: null,
    isStock: null,
    hotelUUID: null,
    placeCountry: null,
    placeCity: null,
    stars: ['0', '1', '2', '3', '4', '5'],
    hotelTypes: [],
    features: [],
    priceRange: [0, 0]
  },
  isFetchingFiltersOptions: false,
  options: {}
}

// getters
const getters = {
  getField,
  isSearchBarMounted: (state) => {
    return state.isSearchBarMounted
  },
  options: (state) => {
    return state.options
  },
  dateRange: (state, getters, rootState) => {
    return {
      start: rootState.route.query.start,
      end: rootState.route.query.end
    }
  },
  autoComplete: (state) => {
    return {
      ...state.autoComplete,
      isMatch: !_.isEmpty(state.autoComplete.callback),
      isLoading: state.autoComplete.isLoading,
      callback: state.autoComplete.callback
    }
  },
  totalNights: (state, getters, rootState) => {
    const start = dayjs(rootState.route.query.start)
    const end = dayjs(rootState.route.query.end)
    return end.diff(start, 'days')
  },
  people: (state) => {
    return {
      adult: state.filters.adult,
      child: state.filters.child,
      childAge: state.filters.childAge
    }
  },
  isFetchingFiltersOptions: (state) => {
    return state.isFetchingFiltersOptions
  },
  defaultFilters: (state) => {
    return state.defaultFilters
  },
  filters: (state) => {
    return state.filters
  },
  queryFilters: (state, getters, rootState, rootGetters) => {
    let filters = {
      currency: rootGetters['user/selectedCurrency'],
      ...state.filters
    }
    if (getters.autoComplete.result) {
      filters = {
        ...filters,
        [`place${_.capitalize(getters.autoComplete.result.type)}`]: getters.autoComplete.result.key
      }
    }
    let result = {}
    for (const key in filters) {
      result = {
        ...result,
        [_.snakeCase([key])]: filters[key]
      }
    }
    return result
  }
}

// actions
const actions = {
  cancelAutoComplete: function ({ commit }) {
    if (state.autoCompleteCancelToken) {
      state.autoCompleteCancelToken.cancel('Cancel AutoComplete fetching.')
    }
  },
  autoCompleteSearchUpdate: _.debounce(function ({ commit, dispatch }, event) {
    console.debug('autoCompleteSearchUpdate:', event.target.value)
    commit('SET_AUTOCOMPLETE_LOADING_MATCH', true)
    commit('SET_AUTOCOMPLETE_SEARCH_VALUE', event.target.value)
    dispatch('fetchAutoCompleteSearchCallback', event.target.value)
  }, 600),
  updateDateRange: function ({ commit, dispatch, rootGetters }, payload) {
    if (!payload) return
    commit('SET_DATE_RANGE', payload)
    // if (rootGetters['hotels/hotelId']) {
    //   dispatch('hotels/fetchHotelRooms', rootGetters['hotels/hotelId'], { root: true })
    // }
  },
  setAutoCompleteResult: function ({ commit }, payload) {
    commit('SET_AUTOCOMPLETE_RESULT', payload)
    commit('SET_FILTERS_PLACE', payload)
    if (!payload) {
      commit('SET_AUTOCOMPLETE_CALLBACK', [])
    }
  },
  fetchAutoCompleteSearchCallback: function ({ commit, state, rootState }, payload) {
    if (state.autoCompleteCancelToken) {
      state.autoCompleteCancelToken.cancel('Cancel AutoComplete Callback fetching.')
    }
    commit('SET_AUTO_COMPLETE_CANCEL_TOKEN')
    return SEARCH.getAutoCompleteV3(payload, state.autoCompleteCancelToken, rootState.isSuper).then(res => {
      console.debug('fetchAutoCompleteSearchCallback', res)
      return res
    })
  },
  fetchAutoCompleteByType: async function ({ commit }, params) {
    return SEARCH.getAutoCompleteByType({
      query: params.search,
      type: params.type,
      pageSize: 6,
      page: params.page ? params.page : 1
    })
  },
  fetchFilterOptions: function ({ commit, rootGetters }) {
    commit('SET_IS_FETCHING_FILTER_OPTIONS', true)
    return SEARCH.getFilterOptions({
      currency: rootGetters['user/selectedCurrency']
    }).then(res => {
      commit('SET_FILTER_OPTIONS', res.data)
      commit('UPDATE_FILTERS_PRICE_RANGE', [Math.floor(Number(res.data.price.min)), Math.ceil(Number(res.data.price.max))])
    })
  },
  getItemByAutoCompleteId: function ({ commit }, autoCompleteId) {
    return SEARCH.getItemByAutoCompleteId(autoCompleteId).then(res => {
      if (res.status === 200) {
        return res.data
      }
    })
  }
}

// mutations
const mutations = {
  updateField,
  UPDATE_FILTERS_PRICE_RANGE: function (state, payload) {
    state.filters.priceRange = [0, 0]
    state.options.price.min = payload[0]
    state.options.price.max = payload[1]
    state.filters.priceRange = payload
  },
  REMOVE_FILTERS_HOTEL_TYPES: function (state, payload) {
    state.filters.hotelTypes.splice(_.indexOf(state.filters.hotelTypes, payload), 1)
  },
  REMOVE_FILTERS_FEATURES: function (state, payload) {
    state.filters.features.splice(_.indexOf(state.filters.features, payload), 1)
  },
  SET_DATE_RANGE: function (state, payload) {
    state.startDate = payload.start
    if (dayjs(payload.start).isSameOrAfter(payload.end)) {
      state.endDate = dayjs(payload.end).add(1, 'day').toDate()
    } else {
      state.endDate = payload.end
    }
    let newQuery = {
      ...router.currentRoute.query
    }
    if (dayjs(payload.start).isValid()) {
      newQuery.start = dayjs(payload.start).format('YYYY-MM-DD').toString()
    } else if (newQuery.start) {
      delete newQuery.start
    }
    if (dayjs(payload.end).isValid()) {
      newQuery.end = dayjs(payload.end).format('YYYY-MM-DD').toString()
    } else if (newQuery.end) {
      delete newQuery.end
    }
    if (router.currentRoute.name) {
      router.push({
        ...router.currentRoute,
        query: {
          ...newQuery
        }
      })
    }
  },
  SET_AUTOCOMPLETE_CALLBACK: function (state, payload) {
    state.autoComplete.callback = payload
  },
  SET_AUTOCOMPLETE_RESULT: function (state, payload) {
    state.autoComplete.result = payload
  },
  SET_IS_FETCHING_FILTER_OPTIONS: function (state, payload) {
    state.isFetchingFiltersOptions = payload
  },
  SET_FILTER_OPTIONS: function (state, payload) {
    state.options = payload
    state.isFetchingFiltersOptions = false
  },
  SET_FILTERS_PLACE: function (state, payload) {
    state.filters.placeCountry = null
    state.filters.placeCity = null
    if (payload) {
      state.filters[`place${_.capitalize(payload.type)}`] = payload.key
    }
  },
  SET_AUTO_COMPLETE_CANCEL_TOKEN: function (state, payload) {
    state.autoCompleteCancelToken = axios.CancelToken.source()
  },
  RESET_DATE_RANGE: function (state) {
    state.startDate = null
    state.endDate = null
    let newQuery = {
      ...router.currentRoute.query
    }
    delete newQuery.start
    delete newQuery.end
    if (router.currentRoute.name) {
      router.push({
        ...router.currentRoute,
        query: {
          ...newQuery
        }
      })
    }
  },
  SET_ADULT_COUNT: function (state, payload) {
    state.filters.adult = payload
  },
  SET_CHILD_COUNT: function (state, payload) {
    state.filters.child = payload
  },
  RESET_CHILD_AGE: function (state) {
    state.filters.childAge = []
  },
  UPDATE_CHILD_AGE_LENGTH: function (state, payload) {
    const { val, oldVal } = payload
    if ((val && !oldVal) || val > oldVal) {
      state.filters.childAge.push(12)
    }
    if (val < oldVal) {
      state.filters.childAge.pop()
    }
  },
  REPLACE_CHILD_AGES: function (state, payload) {
    for (const index in payload) {
      state.filters.childAge[index] = payload[index]
    }
  },
  SET_CHILD_AGE: function (state, payload) {
    const { index, value } = payload
    let newArray = _.cloneDeep(state.filters.childAge)
    newArray[index] = value
    state.filters.childAge = newArray
  },
  SET_SEARCH_BAR_STATUS: function (state, payload) {
    state.isSearchBarMounted = payload
  }
}

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations
}
